import { z } from 'zod'

import type { BoxListParams } from '@/hooks/queries/boxes'

import type { BoxStatus } from '@/client'
import { codeList } from '@/lib/codes'

/**
 * The view state the boxes page reads from the URL.
 *
 * The warehouse is the one required choice: without it there is no list to show,
 * and the page says so rather than guessing. Box and SKU codes are comma-separated
 * text so a pasted list from a spreadsheet works; they are split where the
 * request is built. The vendor, retailer, document and currency picks are ids
 * (or an ISO code) from the warehouse's filter choices.
 */
const STATUSES = [
  'inward',
  'allotted',
  'billed',
  'dispatched',
  'delivered',
] as const satisfies readonly BoxStatus[]

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

const id = z.number().int().positive().optional().catch(undefined)

export const boxSearchSchema = z.object({
  warehouse: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The warehouse whose boxes are listed; nothing loads without it.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the list to show, from 1.'),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Rows per page, 1 to 100.'),
  boxes: z
    .string()
    .optional()
    .catch(undefined)
    .describe('Box codes to show, comma-separated, exact.'),
  skus: z
    .string()
    .optional()
    .catch(undefined)
    .describe('SKU codes a box must hold, comma-separated, exact.'),
  status: z
    .array(z.enum(STATUSES))
    .optional()
    .catch(undefined)
    .describe("The box's stages to narrow to; any stage when absent."),
  from: isoDate.describe('The first day the box was received, YYYY-MM-DD, inclusive.'),
  to: isoDate.describe('The last day the box was received, YYYY-MM-DD, inclusive.'),
  nonEmpty: z.boolean().optional().catch(undefined).describe('Hide boxes that hold nothing.'),
  netBlocked: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe("Take blocked units out of each box's quantity."),
  vendor: id.describe('The vendor id the boxes came from.'),
  retailer: id.describe('The retailer id the boxes are billed to.'),
  invoice: id.describe('The sales invoice id the boxes are on.'),
  vendorInvoice: id.describe('The vendor invoice id that received the boxes.'),
  po: id.describe('The purchase order id the boxes are allotted to.'),
  note: id.describe('The credit/debit note id the boxes are tied to.'),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe('The ISO currency code of the box value.'),
  open: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The id of the box whose contents sheet is open.'),
})

export type BoxSearch = z.infer<typeof boxSearchSchema>

export const BOX_SEARCH_DEFAULTS = { page: 1, limit: 20 } as const

/** Drop any value that equals its default, or is empty, so a URL carries only what differs. */
export function withoutSearchDefaults(search: BoxSearch): BoxSearch {
  const pruned: BoxSearch = {}
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.page !== undefined && search.page !== BOX_SEARCH_DEFAULTS.page)
    pruned.page = search.page
  if (search.limit !== undefined && search.limit !== BOX_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.boxes) pruned.boxes = search.boxes
  if (search.skus) pruned.skus = search.skus
  if (search.status?.length) pruned.status = search.status
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.nonEmpty) pruned.nonEmpty = true
  if (search.netBlocked) pruned.netBlocked = true
  if (search.vendor !== undefined) pruned.vendor = search.vendor
  if (search.retailer !== undefined) pruned.retailer = search.retailer
  if (search.invoice !== undefined) pruned.invoice = search.invoice
  if (search.vendorInvoice !== undefined) pruned.vendorInvoice = search.vendorInvoice
  if (search.po !== undefined) pruned.po = search.po
  if (search.note !== undefined) pruned.note = search.note
  if (search.currency) pruned.currency = search.currency
  if (search.open !== undefined) pruned.open = search.open
  return pruned
}

const startOfDay = (isoDay: string) => `${isoDay}T00:00:00.000Z`

/** The first instant of the day *after*, so an inclusive `to` date is a half-open bound. */
function startOfNextDay(isoDay: string): string {
  const next = new Date(`${isoDay}T00:00:00.000Z`)
  next.setUTCDate(next.getUTCDate() + 1)
  return next.toISOString()
}

/** What the list endpoint is asked for, or `undefined` while no warehouse is chosen. */
export function toListParams(search: BoxSearch): BoxListParams | undefined {
  if (search.warehouse === undefined) return undefined
  return {
    warehouseId: search.warehouse,
    page: search.page ?? BOX_SEARCH_DEFAULTS.page,
    limit: search.limit ?? BOX_SEARCH_DEFAULTS.limit,
    boxCodes: codeList(search.boxes),
    skuCodes: codeList(search.skus),
    status: search.status?.length ? search.status : undefined,
    createdFrom: search.from ? startOfDay(search.from) : undefined,
    createdTo: search.to ? startOfNextDay(search.to) : undefined,
    excludeZero: search.nonEmpty === true,
    excludeBlocked: search.netBlocked === true,
    vendorId: search.vendor,
    retailerId: search.retailer,
    invoiceId: search.invoice,
    vendorInvoiceId: search.vendorInvoice,
    poId: search.po,
    noteId: search.note,
    currency: search.currency,
  }
}
